// pages/TamilLearningSlide.js
// ─────────────────────────────────────────────────────────────
// Phase 1 — Reading Habit Improvement (2026-04-07)
//
// NEW in this version:
//   • Name entry screen (student name + parent/teacher WhatsApp + email)
//   • Per-slide tracking: attempts, correct, firstTryCorrect, timeTaken
//   • Normalized comparison (lowercase, punctuation stripped)
//   • Auto-advance after correct (1.8s)
//   • "Try Again" button when wrong
//   • Attempt counter shown on card
//   • "Skip" button to skip a slide
//   • "Finish Session" button to end early
//   • End-of-session Report Card:
//       - Accuracy %, correct 1st try, retry, not mastered, time
//       - Words to practice list
//       - 📲 Send via WhatsApp (wa.me pre-filled message)
//       - 📧 Send via Email (mailto: pre-filled)
//       - 🔁 Practice Again | 🏠 Home
//
// All existing TTS + Speech Recognition + iOS/Android compatibility preserved.
// ─────────────────────────────────────────────────────────────
import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { API_HEADERS } from "./config";

// ── Colour map per picture filename prefix ───────────────────────────────────
const CARD_COLORS = {
  "01": "#1a7a4a", "02": "#1a7a4a",
  "03": "#3a3ab0", "04": "#5a3dbf",
  "05": "#9a6010", "06": "#9a6010",
  "07": "#8b3020", "08": "#8b3020",
  "09": "#1a5fa0",
  "10": "#2e7d32", "11": "#2e7d32",
  "12": "#a0107a",
};
function getCardColor(picture = "") {
  return CARD_COLORS[picture.slice(0, 2)] || "#444";
}

const IMG_BASE = "/resources/";

// ── Device detection ─────────────────────────────────────────────────────────
const isIOS = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

const isAndroid = () => /Android/i.test(navigator.userAgent);

// ── Best voice for language ──────────────────────────────────────────────────
function getBestVoice(lang) {
  const voices = window.speechSynthesis.getVoices();
  if (!voices.length) return null;
  let v = voices.find(v => v.lang === lang);
  if (v) return v;
  const prefix = lang.split("-")[0];
  v = voices.find(v => v.lang.startsWith(prefix));
  if (v) return v;
  if (lang.startsWith("ta")) {
    v = voices.find(v => v.lang === "en-IN") || voices.find(v => v.lang.startsWith("en"));
    if (v) return v;
  }
  return null;
}

// ── Wait for voices (iOS/Android need this) ──────────────────────────────────
function waitForVoices() {
  return new Promise((resolve) => {
    const voices = window.speechSynthesis.getVoices();
    if (voices.length > 0) { resolve(voices); return; }
    const handler = () => {
      window.speechSynthesis.removeEventListener("voiceschanged", handler);
      resolve(window.speechSynthesis.getVoices());
    };
    window.speechSynthesis.addEventListener("voiceschanged", handler);
    setTimeout(() => { resolve(window.speechSynthesis.getVoices()); }, 2000);
  });
}

// ── iOS TTS keep-alive ───────────────────────────────────────────────────────
let keepAliveTimer = null;
function startKeepAlive() {
  if (!isIOS()) return;
  stopKeepAlive();
  keepAliveTimer = setInterval(() => {
    if (window.speechSynthesis.speaking) return;
    window.speechSynthesis.pause();
    window.speechSynthesis.resume();
  }, 10000);
}
function stopKeepAlive() {
  if (keepAliveTimer) { clearInterval(keepAliveTimer); keepAliveTimer = null; }
}

// ── Helpers ──────────────────────────────────────────────────────────────────
function formatTimeSecs(secs) {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  if (m === 0) return s + " sec";
  return m + " min " + s + " sec";
}

function formatTimeMinSec(secs) {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return String(m).padStart(2, "0") + ":" + String(s).padStart(2, "0");
}

// Normalize text for comparison: lowercase, no punctuation
function normalizeText(str) {
  return str.toLowerCase().trim().replace(/[.,!?;:'"()]/g, "").replace(/\s+/g, " ");
}

// ── Spinner ──────────────────────────────────────────────────────────────────
function Spinner() {
  return (
    <div style={{ display:"flex", justifyContent:"center", padding:"60px 0" }}>
      <div style={{
        width:48, height:48, borderRadius:"50%",
        border:"4px solid #e0e0e0", borderTopColor:"#1a7a4a",
        animation:"spin 0.8s linear infinite",
      }}/>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

// ── Word-level diff ──────────────────────────────────────────────────────────
function WordDiff({ expected, spoken }) {
  if (!spoken) return null;
  const expWords = expected.trim().split(/\s+/);
  const spkWords = spoken.trim().split(/\s+/);
  return (
    <div style={{ marginTop:8, display:"flex", flexWrap:"wrap", gap:4 }}>
      {spkWords.map((word, i) => {
        const match = i < expWords.length &&
          normalizeText(word) === normalizeText(expWords[i]);
        return (
          <span key={i} style={{
            padding:"2px 10px", borderRadius:20,
            background: match ? "#d4f7e0" : "#ffe0e0",
            color:      match ? "#1a5c33" : "#8b2020",
            fontFamily:"'Noto Sans Tamil',sans-serif",
            fontSize:20, fontWeight:600,
          }}>{word}</span>
        );
      })}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// NAME ENTRY SCREEN
// ════════════════════════════════════════════════════════════════
function NameEntry({ slides, onStart }) {
  const [name,  setName]  = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");

  function handleSubmit(e) {
    e.preventDefault();
    if (!name.trim()) return;
    onStart({ name: name.trim(), phone: phone.trim(), email: email.trim() });
  }

  const subjectName = slides[0]?.subject_name || "Tamil Learning";
  const gradeName   = slides[0]?.grade_name   || "";
  const total       = slides.length;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+Tamil:wght@400;700&family=Baloo+2:wght@500;700;800&display=swap');
        *, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }
        body { background:#f0f4f8; }
        .ne-root {
          min-height:100vh; display:flex; align-items:center; justify-content:center;
          background:linear-gradient(135deg,#f0f4f8 0%,#dce8f5 100%);
          padding:24px 16px; font-family:'Baloo 2',sans-serif;
        }
        .ne-card {
          background:#fff; border-radius:28px;
          box-shadow:0 8px 40px rgba(0,0,0,0.12);
          padding:36px 28px; width:100%; max-width:400px;
        }
        .ne-input {
          width:100%; padding:12px 16px; border-radius:12px;
          border:1.5px solid #ddd; font-size:15px;
          font-family:'Baloo 2',sans-serif; font-weight:500;
          margin-bottom:12px; outline:none; transition:border .2s; display:block;
        }
        .ne-input:focus { border-color:#1a7a4a; }
        .ne-btn {
          width:100%; padding:14px; border-radius:14px; border:none;
          background:#1a7a4a; color:#fff; font-size:16px; font-weight:700;
          font-family:'Baloo 2',sans-serif; cursor:pointer; margin-top:6px;
          -webkit-tap-highlight-color:transparent; touch-action:manipulation;
        }
        .ne-btn:active { background:#15643d; }
        .ne-optional {
          font-size:11px; font-weight:700; color:#aaa;
          text-transform:uppercase; letter-spacing:.4px; margin-bottom:6px; display:block;
        }
        .ne-info-box {
          background:#e8f5e9; border:1.5px solid #a5d6a7;
          border-radius:12px; padding:12px 14px; margin-bottom:22px; font-size:13px; color:#2e7d32;
        }
      `}</style>

      <div className="ne-root">
        <div className="ne-card">
          <div style={{ textAlign:"center", marginBottom:24 }}>
            <div style={{ fontSize:44, marginBottom:8 }}>📖</div>
            <div style={{ fontSize:22, fontWeight:800, color:"#1a237e" }}>
              Reading Practice
            </div>
            <div style={{ fontSize:13, color:"#888", marginTop:4 }}>
              {subjectName}{gradeName ? " · " + gradeName : ""} · {total} slides
            </div>
          </div>

          <div className="ne-info-box">
            📊 At the end of your session, a <strong>report</strong> will be generated showing
            your accuracy, speed, and words to practice — shareable via WhatsApp or Email.
          </div>

          <form onSubmit={handleSubmit}>
            <span className="ne-optional">Student Name *</span>
            <input
              className="ne-input"
              type="text"
              placeholder="Enter student name"
              value={name}
              onChange={e => setName(e.target.value)}
              required
              autoFocus
            />

            <span className="ne-optional">Parent / Teacher WhatsApp (optional)</span>
            <input
              className="ne-input"
              type="tel"
              placeholder="e.g. 919876543210"
              value={phone}
              onChange={e => setPhone(e.target.value)}
            />

            <span className="ne-optional">Email (optional)</span>
            <input
              className="ne-input"
              type="email"
              placeholder="parent@gmail.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
            />

            <button className="ne-btn" type="submit">
              🚀 Start Reading Session
            </button>
          </form>
        </div>
      </div>
    </>
  );
}

// ════════════════════════════════════════════════════════════════
// REPORT CARD SCREEN
// ════════════════════════════════════════════════════════════════
function ReportCard({
  sessionLog, slides, studentName,
  recipientEmail, recipientPhone,
  totalTimeSecs, onHome, onRestart,
}) {
  const totalSlides      = slides.length;
  const attempted        = sessionLog.length;
  const correctCount     = sessionLog.filter(r => r.correct).length;
  const firstTryCorrect  = sessionLog.filter(r => r.firstTryCorrect).length;
  const correctWithRetry = correctCount - firstTryCorrect;
  const notMastered      = sessionLog.filter(r => !r.correct).length;
  const skipped          = totalSlides - attempted;
  const accuracyPct      = attempted > 0 ? Math.round((correctCount / attempted) * 100) : 0;
  const date             = new Date().toLocaleDateString("en-IN", {
    day:"2-digit", month:"short", year:"numeric",
  });

  // Words needing extra practice (wrong OR took > 2 tries)
  const wordsToFocus = sessionLog
    .filter(r => !r.correct || r.attempts > 2)
    .sort((a, b) => b.attempts - a.attempts);

  // Accuracy ring color
  const accColor = accuracyPct >= 80 ? "#1a7a4a"
                 : accuracyPct >= 50 ? "#e07b00"
                 : "#c62828";

  // ── Build shareable text ─────────────────────────────────────
  function buildReportText() {
    const subjectName = slides[0]?.subject_name || "Tamil Learning";
    let t = "";
    t += "📚 *VirtualKalvi Reading Report*\n";
    t += "─────────────────────\n";
    t += `👤 Student : ${studentName}\n`;
    t += `📅 Date    : ${date}\n`;
    t += `📖 Subject : ${subjectName}\n`;
    t += "─────────────────────\n";
    t += "📊 *Session Results*\n";
    t += `• Total Slides        : ${totalSlides}\n`;
    t += `• ✅ 1st Try Correct  : ${firstTryCorrect}\n`;
    t += `• 🔄 Correct (retry)  : ${correctWithRetry}\n`;
    t += `• ❌ Not Mastered     : ${notMastered}\n`;
    if (skipped > 0)
      t += `• ⏭️ Skipped          : ${skipped}\n`;
    t += `• 🎯 Accuracy         : ${accuracyPct}%\n`;
    t += `• ⏱️ Total Time       : ${formatTimeSecs(totalTimeSecs)}\n`;
    if (wordsToFocus.length > 0) {
      t += "─────────────────────\n";
      t += "📝 *Practice Again*\n";
      wordsToFocus.forEach(w => {
        t += `• ${w.expectedText}  (${w.attempts} ${w.attempts === 1 ? "try" : "tries"}) ${w.correct ? "✅" : "❌"}\n`;
      });
    }
    t += "─────────────────────\n";
    t += "🌟 Powered by VirtualKalvi";
    return t;
  }

  function handleWhatsApp() {
    const phone = (recipientPhone || "").replace(/[^0-9]/g, "");
    const url   = `https://wa.me/${phone}?text=${encodeURIComponent(buildReportText())}`;
    window.open(url, "_blank");
  }

  function handleEmail() {
    const subject = `VirtualKalvi Reading Report — ${studentName} — ${date}`;
    const body    = buildReportText().replace(/\*/g, ""); // strip markdown bold for email
    const to      = recipientEmail || "";
    window.open(
      `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`,
      "_blank"
    );
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+Tamil:wght@400;700&family=Baloo+2:wght@500;700;800&display=swap');
        *, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }
        body { background:#f0f4f8; }
        .rc-root {
          min-height:100vh;
          background:linear-gradient(135deg,#f0f4f8 0%,#dce8f5 100%);
          display:flex; flex-direction:column; align-items:center;
          padding:24px 16px 48px; font-family:'Baloo 2',sans-serif;
        }
        .rc-card {
          width:100%; max-width:420px; background:#fff; border-radius:28px;
          box-shadow:0 8px 40px rgba(0,0,0,0.13); overflow:hidden;
          padding:28px 22px 24px;
          animation:popIn .4s cubic-bezier(.34,1.56,.64,1) both;
        }
        @keyframes popIn { from{opacity:0;transform:scale(0.88)} to{opacity:1;transform:scale(1)} }
        .rc-stat-grid {
          display:grid; grid-template-columns:1fr 1fr;
          gap:10px; margin:16px 0;
        }
        .rc-stat-box {
          background:#f8faff; border-radius:14px;
          padding:13px 10px; text-align:center;
          border:1.5px solid #e8eaf6;
        }
        .rc-stat-num  { font-size:24px; font-weight:800; line-height:1.1; }
        .rc-stat-lbl  { font-size:11px; font-weight:600; color:#888; margin-top:4px; text-transform:uppercase; letter-spacing:.4px; }
        .rc-word-row  {
          display:flex; align-items:center; justify-content:space-between;
          padding:9px 13px; background:#fff8f0; border-radius:10px;
          border:1.5px solid #f5c6a0; margin-bottom:7px;
          font-family:'Noto Sans Tamil',sans-serif; font-size:20px;
        }
        .rc-word-badge {
          font-size:12px; color:#aaa; font-family:'Baloo 2',sans-serif; white-space:nowrap;
        }
        .rc-btn {
          width:100%; padding:14px; border-radius:14px; border:none;
          font-family:'Baloo 2',sans-serif; font-size:15px; font-weight:700;
          cursor:pointer; margin-bottom:10px; transition:opacity .15s;
          -webkit-tap-highlight-color:transparent; touch-action:manipulation;
          display:flex; align-items:center; justify-content:center; gap:8px;
        }
        .rc-btn:active { opacity:.82; }
        .rc-btn-wa    { background:#25D366; color:#fff; }
        .rc-btn-email { background:#1a5fa0; color:#fff; }
        .rc-btn-sm    { background:#e8eaf6; color:#1a237e; font-size:14px; padding:12px; }
        .rc-2col      { display:grid; grid-template-columns:1fr 1fr; gap:10px; }
        .rc-section-title {
          font-size:11px; font-weight:700; color:#888;
          text-transform:uppercase; letter-spacing:.5px; margin-bottom:8px;
        }
        .rc-divider { border-top:1.5px solid #f0f0f0; margin:18px 0; }
      `}</style>

      <div className="rc-root">
        <div className="rc-card">

          {/* ── Header ── */}
          <div style={{ textAlign:"center", marginBottom:18 }}>
            <div style={{ fontSize:44, marginBottom:6 }}>
              {accuracyPct >= 80 ? "🏆" : accuracyPct >= 50 ? "👍" : "💪"}
            </div>
            <div style={{ fontSize:22, fontWeight:800, color:"#1a237e" }}>
              Session Complete!
            </div>
            <div style={{ fontSize:13, color:"#888", marginTop:4 }}>
              {studentName} · {date}
            </div>
          </div>

          {/* ── Accuracy ring ── */}
          <div style={{
            textAlign:"center", borderRadius:20, padding:"18px 0",
            background: accuracyPct >= 80 ? "#e8f5e9" : accuracyPct >= 50 ? "#fff8e1" : "#ffebee",
            border:`2px solid ${accColor}`, marginBottom:2,
          }}>
            <div style={{ fontSize:56, fontWeight:800, color:accColor, lineHeight:1 }}>
              {accuracyPct}%
            </div>
            <div style={{ fontSize:13, color:accColor, fontWeight:700, marginTop:5 }}>
              Overall Accuracy · {attempted}/{totalSlides} slides attempted
            </div>
          </div>

          {/* ── Stats grid ── */}
          <div className="rc-stat-grid">
            <div className="rc-stat-box">
              <div className="rc-stat-num" style={{ color:"#1a7a4a" }}>{firstTryCorrect}</div>
              <div className="rc-stat-lbl">✅ 1st Try</div>
            </div>
            <div className="rc-stat-box">
              <div className="rc-stat-num" style={{ color:"#e07b00" }}>{correctWithRetry}</div>
              <div className="rc-stat-lbl">🔄 With Retry</div>
            </div>
            <div className="rc-stat-box">
              <div className="rc-stat-num" style={{ color:"#c62828" }}>{notMastered}</div>
              <div className="rc-stat-lbl">❌ Not Mastered</div>
            </div>
            <div className="rc-stat-box">
              <div className="rc-stat-num" style={{ color:"#3949ab", fontSize:18 }}>
                {formatTimeSecs(totalTimeSecs)}
              </div>
              <div className="rc-stat-lbl">⏱ Total Time</div>
            </div>
          </div>

          {skipped > 0 && (
            <div style={{
              textAlign:"center", fontSize:13, color:"#888",
              background:"#f5f5f5", borderRadius:10, padding:"8px",
              marginBottom:4,
            }}>
              ⏭️ {skipped} slide{skipped > 1 ? "s" : ""} skipped
            </div>
          )}

          {/* ── Words to practice ── */}
          {wordsToFocus.length > 0 && (
            <>
              <div className="rc-divider" />
              <div className="rc-section-title">📝 Practice These Again</div>
              <div style={{ maxHeight:180, overflowY:"auto", paddingRight:2 }}>
                {wordsToFocus.map((w, i) => (
                  <div key={i} className="rc-word-row">
                    <span style={{ color: w.correct ? "#8b4513" : "#8b2020" }}>
                      {w.expectedText}
                    </span>
                    <span className="rc-word-badge">
                      {w.attempts} {w.attempts === 1 ? "try" : "tries"} {w.correct ? "✅" : "❌"}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* ── Share buttons ── */}
          <div className="rc-divider" />
          <div className="rc-section-title">📤 Share Report</div>

          <button className="rc-btn rc-btn-wa" onClick={handleWhatsApp}>
            📲 Send via WhatsApp
          </button>
          <button className="rc-btn rc-btn-email" onClick={handleEmail}>
            📧 Send via Email
          </button>

          <div className="rc-2col">
            <button className="rc-btn rc-btn-sm" onClick={onRestart} style={{ margin:0 }}>
              🔁 Practice Again
            </button>
            <button className="rc-btn rc-btn-sm" onClick={onHome} style={{ margin:0 }}>
              🏠 Home
            </button>
          </div>

        </div>
      </div>
    </>
  );
}

// ════════════════════════════════════════════════════════════════
// MAIN COMPONENT — TamilLearningSlide
// ════════════════════════════════════════════════════════════════
export default function TamilLearningSlide() {
  const location       = useLocation();
  const navigate       = useNavigate();
  const [searchParams] = useSearchParams();

  const apiUrl = searchParams.get("api") || location.state?.apiUrl || "";

  // ── Data ──────────────────────────────────────────────────────
  const [slides,   setSlides]   = useState([]);
  const [current,  setCurrent]  = useState(0);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);

  // ── App phase ─────────────────────────────────────────────────
  // "name" → student enters details
  // "learning" → slide-by-slide reading
  // "report" → end-of-session report card
  const [phase, setPhase] = useState("name");

  // ── Student info ──────────────────────────────────────────────
  const [studentName,     setStudentName]     = useState("");
  const [recipientPhone,  setRecipientPhone]  = useState("");
  const [recipientEmail,  setRecipientEmail]  = useState("");

  // ── Session tracking ──────────────────────────────────────────
  const [sessionLog,    setSessionLog]    = useState([]); // array of per-slide records
  const [slideAttempts, setSlideAttempts] = useState(0); // attempts for current slide (render)

  // Refs for use inside closures (avoid stale state)
  const slideAttemptsRef    = useRef(0);
  const slideStartTimeRef   = useRef(Date.now());
  const sessionStartTimeRef = useRef(Date.now());
  const currentRef          = useRef(0);

  // ── TTS / Speech state ────────────────────────────────────────
  const [speaking,      setSpeaking]      = useState(false);
  const [listening,     setListening]     = useState(false);
  const [spokenText,    setSpokenText]    = useState("");
  const [resultMsg,     setResultMsg]     = useState(null); // null | "correct" | "wrong"
  const [autoPlay,      setAutoPlay]      = useState(false);
  const [voicesReady,   setVoicesReady]   = useState(false);
  const [micSupported,  setMicSupported]  = useState(false);
  const [micDenied,     setMicDenied]     = useState(false);
  const [autoAdvancing, setAutoAdvancing] = useState(false); // true during 1.8s delay after correct

  const recognitionRef = useRef(null);
  const autoPlayRef    = useRef(false);
  const autoIdxRef     = useRef(0);

  // ── Keep currentRef in sync ───────────────────────────────────
  useEffect(() => { currentRef.current = current; }, [current]);

  // ── Pre-load voices on mount ──────────────────────────────────
  useEffect(() => {
    waitForVoices().then(() => setVoicesReady(true));
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SR) {
      const onHttps = window.location.protocol === "https:" || window.location.hostname === "localhost";
      setMicSupported(onHttps);
    } else {
      setMicSupported(false);
    }
    return () => stopKeepAlive();
  }, []);

  // ── Fetch slides ──────────────────────────────────────────────
  useEffect(() => {
    if (!apiUrl) {
      setError("No API URL found. Use ?api= in the URL or navigate from Home.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    fetch(apiUrl, { headers: API_HEADERS })
      .then(r => { if (!r.ok) throw new Error(`Server error: HTTP ${r.status}`); return r.json(); })
      .then(data => { setSlides(data || []); setCurrent(0); setLoading(false); })
      .catch(e  => { setError(e.message); setLoading(false); });
  }, [apiUrl]);

  // ── Sync autoPlayRef ──────────────────────────────────────────
  useEffect(() => { autoPlayRef.current = autoPlay; }, [autoPlay]);

  // ── Reset per-slide state on slide change ─────────────────────
  useEffect(() => {
    if (autoPlayRef.current) return;
    stopListening();
    setSpokenText("");
    setResultMsg(null);
    setAutoAdvancing(false);
    slideAttemptsRef.current = 0;
    setSlideAttempts(0);
    slideStartTimeRef.current = Date.now();
    window.speechSynthesis?.cancel();
    setSpeaking(false);
  }, [current]); // eslint-disable-line

  // ── Core speak ────────────────────────────────────────────────
  const speakText = useCallback((text, lang) => {
    return new Promise(async (resolve) => {
      if (!window.speechSynthesis) { resolve(); return; }
      await waitForVoices();
      window.speechSynthesis.cancel();
      if (isIOS()) {
        window.speechSynthesis.resume();
        await new Promise(r => setTimeout(r, 80));
      }
      const utter  = new SpeechSynthesisUtterance(text);
      utter.lang   = lang;
      utter.rate   = isIOS() ? 0.75 : 0.8;
      utter.volume = 1;
      utter.pitch  = 1;
      const voice  = getBestVoice(lang);
      if (voice) utter.voice = voice;
      let resolved = false;
      const done = () => {
        if (!resolved) { resolved = true; setSpeaking(false); resolve(); }
      };
      utter.onstart = () => setSpeaking(true);
      utter.onend   = done;
      utter.onerror = (e) => {
        if (e.error !== "interrupted") console.warn("TTS error:", e.error);
        done();
      };
      const safetyMs = Math.min(text.length * 200 + 3000, 15000);
      setTimeout(done, safetyMs);
      window.speechSynthesis.speak(utter);
      if (isIOS()) startKeepAlive();
    });
  }, []);

  const speakSlide = useCallback((idx) => {
    const slide = slides[idx];
    if (!slide) return Promise.resolve();
    const text = slide.question_text_read || slide.question_text;
    const lang = (slide.medium || "").toLowerCase() === "tamil" ? "ta-IN" : "en-US";
    return speakText(text, lang);
  }, [slides, speakText]);

  // ── Manual Read ───────────────────────────────────────────────
  const handleRead = useCallback(() => {
    if (autoPlay) return;
    speakSlide(current);
  }, [autoPlay, speakSlide, current]);

  // ── Autoplay ──────────────────────────────────────────────────
  useEffect(() => {
    if (!autoPlay || slides.length === 0) return;
    let stopped = false;
    autoIdxRef.current = current;
    const playNext = async (idx) => {
      if (stopped || !autoPlayRef.current || idx >= slides.length) {
        if (!stopped) setAutoPlay(false);
        stopKeepAlive();
        return;
      }
      setCurrent(idx);
      autoIdxRef.current = idx;
      await new Promise(r => setTimeout(r, isIOS() ? 300 : 600));
      if (stopped || !autoPlayRef.current) return;
      await speakSlide(idx);
      if (stopped || !autoPlayRef.current) return;
      await new Promise(r => setTimeout(r, isIOS() ? 700 : 1200));
      if (stopped || !autoPlayRef.current) return;
      playNext(idx + 1);
    };
    playNext(current);
    return () => {
      stopped = true;
      window.speechSynthesis.cancel();
      setSpeaking(false);
      stopKeepAlive();
    };
  }, [autoPlay]); // eslint-disable-line

  const toggleAutoPlay = () => {
    if (autoPlay) {
      setAutoPlay(false);
      window.speechSynthesis.cancel();
      setSpeaking(false);
      stopKeepAlive();
    } else {
      setSpokenText("");
      setResultMsg(null);
      stopListening();
      setAutoPlay(true);
    }
  };

  // ── Record slide result into sessionLog ───────────────────────
  function recordSlide(isCorrect, spokenFinal, attempts, expectedText, slide) {
    const timeSecs = Math.round((Date.now() - slideStartTimeRef.current) / 1000);
    const record = {
      slideIndex:     currentRef.current,
      picture:        slide?.picture || "",
      expectedText:   expectedText,
      attempts:       attempts,
      correct:        isCorrect,
      firstTryCorrect: attempts === 1 && isCorrect,
      timeTakenSecs:  timeSecs,
      spokenFinal:    spokenFinal,
    };
    setSessionLog(prev => {
      // Replace any existing record for this slide
      const filtered = prev.filter(r => r.slideIndex !== currentRef.current);
      return [...filtered, record];
    });
  }

  // ── Speech recognition (modified for tracking) ────────────────
  const startListening = useCallback(() => {
    const slide = slides[current];
    if (!slide) return;

    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      alert(
        isIOS()
          ? "Speech recognition requires Safari on iOS 14.5 or later."
          : isAndroid()
          ? "Speech recognition requires Chrome on Android."
          : "Speech recognition is not supported in this browser."
      );
      return;
    }

    const lang = (slide.medium || "").toLowerCase() === "tamil" ? "ta-IN" : "en-US";
    const rec  = new SR();
    rec.lang           = lang;
    rec.interimResults = true;
    rec.continuous     = false;
    recognitionRef.current = rec;

    setSpokenText("");
    setResultMsg(null);
    setListening(true);

    rec.onresult = (e) => {
      let interim = "", final = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) final += t;
        else interim += t;
      }
      setSpokenText(final || interim);

      if (final) {
        const expected  = (slide.question_text_read || slide.question_text).trim();
        const isCorrect = normalizeText(final) === normalizeText(expected);

        // Increment attempt count (use ref to avoid stale closure)
        const newAttempts = slideAttemptsRef.current + 1;
        slideAttemptsRef.current = newAttempts;
        setSlideAttempts(newAttempts);

        setResultMsg(isCorrect ? "correct" : "wrong");

        // Record this attempt in session log
        recordSlide(isCorrect, final.trim(), newAttempts, expected, slide);

        if (isCorrect) {
          // Auto-advance to next slide after 1.8s
          setAutoAdvancing(true);
          const idx      = currentRef.current;
          const numSlides = slides.length;
          setTimeout(() => {
            setAutoAdvancing(false);
            if (idx < numSlides - 1) {
              setCurrent(idx + 1);
            } else {
              // All slides done → show report
              setPhase("report");
            }
          }, 1800);
        }
      }
    };

    rec.onerror = (e) => {
      console.warn("SpeechRecognition error:", e.error);
      setListening(false);
    };
    rec.onend = () => setListening(false);

    if (isIOS()) {
      navigator.mediaDevices?.getUserMedia({ audio: true })
        .then(() => {
          try { rec.start(); }
          catch(e) { console.warn("rec.start() failed:", e); setListening(false); }
        })
        .catch((err) => {
          console.warn("Mic permission denied:", err);
          setMicDenied(true);
          setListening(false);
        });
    } else {
      try { rec.start(); }
      catch(e) { console.warn("rec.start() failed:", e); setListening(false); }
    }
  }, [slides, current]); // eslint-disable-line

  const stopListening = useCallback(() => {
    try { recognitionRef.current?.stop(); } catch(_) {}
    setListening(false);
  }, []);

  const clearSpoken = useCallback(() => {
    stopListening();
    setSpokenText("");
    setResultMsg(null);
  }, [stopListening]);

  const deleteLastWord = useCallback(() => {
    setSpokenText(prev => {
      const words = prev.trim().split(/\s+/);
      words.pop();
      return words.join(" ");
    });
    setResultMsg(null);
  }, []);

  // ── Try Again (on wrong) ──────────────────────────────────────
  function handleTryAgain() {
    setSpokenText("");
    setResultMsg(null);
    // slideAttempts intentionally NOT reset — carry the count forward
  }

  // ── Skip slide ────────────────────────────────────────────────
  function handleSkip() {
    if (current < slides.length - 1) {
      setCurrent(c => c + 1);
    } else {
      setPhase("report");
    }
  }

  // ── Finish session early ──────────────────────────────────────
  function handleFinishSession() {
    if (window.confirm("End this session and see your report?")) {
      setPhase("report");
    }
  }

  // ── Nav ───────────────────────────────────────────────────────
  const prev = () => { if (!autoPlay && !autoAdvancing) setCurrent(c => Math.max(0, c - 1)); };
  const next = () => { if (!autoPlay && !autoAdvancing) setCurrent(c => Math.min(slides.length - 1, c + 1)); };

  // ────────────────────────────────────────────────────────────
  // RENDER: loading / error (before phase check)
  // ────────────────────────────────────────────────────────────
  if (loading) return <Spinner />;
  if (error)   return (
    <div style={{ padding:32, color:"#c00", fontFamily:"sans-serif", textAlign:"center" }}>
      <div style={{ fontSize:40, marginBottom:12 }}>⚠️</div>
      <div>{error}</div>
      <button onClick={() => navigate(-1)}
        style={{ marginTop:20, padding:"10px 24px", borderRadius:24, border:"none",
          background:"#1a7a4a", color:"white", cursor:"pointer", fontSize:15 }}>
        ← Go Back
      </button>
    </div>
  );
  if (!slides.length) return (
    <div style={{ padding:32, textAlign:"center", fontFamily:"sans-serif" }}>No slides found.</div>
  );

  // ── PHASE: name ───────────────────────────────────────────────
  if (phase === "name") {
    return (
      <NameEntry
        slides={slides}
        onStart={({ name, phone, email }) => {
          setStudentName(name);
          setRecipientPhone(phone);
          setRecipientEmail(email);
          sessionStartTimeRef.current = Date.now();
          slideStartTimeRef.current   = Date.now();
          slideAttemptsRef.current    = 0;
          setSessionLog([]);
          setSlideAttempts(0);
          setCurrent(0);
          setPhase("learning");
        }}
      />
    );
  }

  // ── PHASE: report ─────────────────────────────────────────────
  if (phase === "report") {
    const totalTimeSecs = Math.round((Date.now() - sessionStartTimeRef.current) / 1000);
    return (
      <ReportCard
        sessionLog={sessionLog}
        slides={slides}
        studentName={studentName}
        recipientEmail={recipientEmail}
        recipientPhone={recipientPhone}
        totalTimeSecs={totalTimeSecs}
        onHome={() => navigate(-1)}
        onRestart={() => {
          setSessionLog([]);
          setSlideAttempts(0);
          slideAttemptsRef.current = 0;
          setCurrent(0);
          setPhase("name");
        }}
      />
    );
  }

  // ── PHASE: learning ───────────────────────────────────────────
  const slide     = slides[current];
  const cardColor = getCardColor(slide.picture);
  const isTamil   = (slide.medium || "").toLowerCase() === "tamil";
  const expected  = (slide.question_text_read || slide.question_text).trim();
  const progressPct = ((current + 1) / slides.length) * 100;

  // Completed slides in session (for top-bar counter)
  const completedCount = sessionLog.filter(r => r.correct).length;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+Tamil:wght@400;700&family=Baloo+2:wght@500;700&display=swap');
        *, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }
        body { background:#f0f4f8; -webkit-text-size-adjust:100%; }

        .slide-root {
          min-height:100vh;
          background:linear-gradient(135deg,#f0f4f8 0%,#dce8f5 100%);
          display:flex; flex-direction:column; align-items:center;
          padding:0 0 40px; font-family:'Baloo 2',sans-serif;
          -webkit-overflow-scrolling:touch;
        }

        /* ── Top bar ── */
        .top-bar {
          width:100%; background:#1a237e; color:#fff;
          padding:11px 16px; display:flex; align-items:center;
          justify-content:space-between; gap:8px;
        }
        .top-bar-left  { display:flex; align-items:center; gap:8px; }
        .top-bar-right { display:flex; align-items:center; gap:8px; }
        .tb-btn {
          background:rgba(255,255,255,0.18); border:none; border-radius:8px;
          color:#fff; font-family:'Baloo 2',sans-serif; font-size:13px;
          font-weight:700; padding:6px 12px; cursor:pointer; min-height:36px;
          -webkit-tap-highlight-color:transparent;
        }
        .tb-score {
          background:rgba(255,255,255,0.15); border-radius:8px;
          padding:5px 10px; font-size:13px; font-weight:700;
          display:flex; align-items:center; gap:5px;
        }

        /* ── Progress bar ── */
        .prog-bar-wrap { width:100%; height:5px; background:#e0e0e0; }
        .prog-bar-fill { height:100%; background:#43a047; transition:width 0.4s ease; }

        .slide-header {
          width:100%; max-width:420px; margin:12px 0 8px; text-align:center; padding:0 16px;
        }
        .slide-header .institution { font-size:12px; color:#888; }
        .slide-header .subject     { font-size:15px; font-weight:700; color:#1a3a5c; margin-top:2px; }
        .slide-header .progress    { font-size:12px; color:#aaa; margin-top:3px; }

        .autoplay-bar { width:100%; max-width:420px; height:4px; background:#dde; border-radius:2px; margin-bottom:10px; overflow:hidden; }
        .autoplay-bar-fill { height:100%; background:#f59e0b; border-radius:2px; transition:width 0.4s ease; }

        .slide-card {
          width:calc(100% - 32px); max-width:420px; background:white; border-radius:28px;
          box-shadow:0 8px 40px rgba(0,0,0,0.12); overflow:hidden;
          animation:slideIn 0.35s ease;
        }
        @keyframes slideIn { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:none} }

        .pic-area {
          display:flex; justify-content:center; align-items:center;
          padding:28px 24px 20px; background:#fafafa;
          border-bottom:1px solid #f0f0f0; min-height:200px;
        }
        .pic-area img {
          max-width:160px; max-height:160px; border-radius:16px;
          box-shadow:0 4px 20px rgba(0,0,0,0.10); object-fit:contain;
        }
        .no-img {
          width:130px; height:130px; border-radius:20px;
          display:flex; align-items:center; justify-content:center;
          font-size:68px; font-family:'Noto Sans Tamil',sans-serif;
          color:white; font-weight:700;
        }

        .question-area { padding:18px 28px 14px; text-align:center; }
        .question-text {
          font-family:'Noto Sans Tamil',sans-serif;
          font-size:52px; font-weight:700; color:#1a2a3a; line-height:1.2;
        }
        .medium-badge {
          display:inline-block; margin-top:8px; padding:2px 14px;
          border-radius:20px; font-size:12px; font-weight:600;
          letter-spacing:.5px; text-transform:uppercase;
        }

        /* ── Attempt counter ── */
        .attempt-tag {
          display:inline-block; margin-top:6px; padding:3px 12px;
          border-radius:20px; font-size:12px; font-weight:700;
          background:#fff8e1; color:#8b6000; border:1.5px solid #ffe082;
        }

        .voices-notice {
          margin:0 20px 10px; padding:8px 14px; border-radius:10px;
          background:#fffde7; border:1px solid #ffe082;
          font-size:13px; color:#7a5c00; text-align:center;
        }

        .actions {
          display:flex; gap:8px; justify-content:center;
          padding:0 16px 14px; flex-wrap:wrap;
        }
        .btn {
          display:flex; align-items:center; gap:6px;
          padding:0 18px; min-height:44px;
          border-radius:50px; border:none; cursor:pointer;
          font-family:'Baloo 2',sans-serif; font-size:14px; font-weight:600;
          transition:all 0.2s;
          -webkit-tap-highlight-color:transparent; touch-action:manipulation;
        }
        .btn:active { transform:scale(0.96); }
        .btn:disabled { opacity:.4; cursor:not-allowed; }

        .btn-read  { background:#1a7a4a; color:white; }
        .btn-read:hover:not(:disabled)  { background:#15643d; }
        .btn-read.speaking { background:#0d4d30; animation:pulse 1s infinite; }

        .btn-mic   { background:#1a5fa0; color:white; }
        .btn-mic:hover:not(:disabled)   { background:#154d84; }
        .btn-mic.listening { background:#c0392b; animation:pulse .8s infinite; }

        .btn-autoplay { background:#f59e0b; color:#1a1a1a; }
        .btn-autoplay:hover:not(:disabled) { background:#d97706; color:white; }
        .btn-autoplay.playing { background:#dc2626; color:white; animation:pulse 1s infinite; }

        .btn-skip { background:#f0f0f0; color:#555; font-size:13px; }
        .btn-skip:hover:not(:disabled) { background:#e0e0e0; }

        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.7} }

        .autoplay-reading {
          margin:0 20px 14px; padding:10px 16px;
          background:#fffbeb; border:1.5px solid #f59e0b;
          border-radius:12px; text-align:center;
          font-size:14px; color:#92400e; font-weight:600;
          animation:fadeIn .3s ease;
        }

        /* ── Auto-advance notice ── */
        .advancing-notice {
          margin:0 16px 10px; padding:10px 16px;
          background:#e8f5e9; border:1.5px solid #a5d6a7;
          border-radius:12px; text-align:center;
          font-size:14px; color:#2e7d32; font-weight:700;
          animation:fadeIn .3s ease;
          display:flex; align-items:center; justify-content:center; gap:8px;
        }

        .speech-box {
          margin:0 16px 14px; background:#f8faff;
          border:2px solid #d0e4ff; border-radius:16px;
          padding:12px 14px 10px; min-height:60px;
        }
        .speech-label {
          font-size:11px; font-weight:700; color:#888;
          text-transform:uppercase; letter-spacing:.8px; margin-bottom:5px;
        }
        .speech-input {
          font-family:'Noto Sans Tamil',sans-serif;
          font-size:20px; color:#1a2a3a; min-height:28px; word-break:break-word;
        }
        .speech-controls { display:flex; gap:8px; margin-top:8px; flex-wrap:wrap; }
        .btn-sm {
          padding:0 14px; min-height:36px; border-radius:20px; border:none;
          font-family:'Baloo 2',sans-serif; font-size:13px; font-weight:600;
          cursor:pointer; transition:all .18s;
          -webkit-tap-highlight-color:transparent; touch-action:manipulation;
        }
        .btn-sm:active { transform:scale(.95); }
        .btn-del-word { background:#fff3cd; color:#856404; border:1px solid #ffc107; }
        .btn-clear    { background:#ffe0e0; color:#8b2020; border:1px solid #f5a5a5; }

        .result-banner {
          margin:0 16px 14px; padding:10px 18px; border-radius:14px;
          text-align:center; font-size:15px; font-weight:700;
          animation:fadeIn .4s ease;
        }
        .result-correct { background:#d4f7e0; color:#1a5c33; border:1.5px solid #6fcf97; }
        .result-wrong   {
          background:#ffe0e0; color:#8b2020; border:1.5px solid #f5a5a5;
          display:flex; flex-direction:column; align-items:center; gap:8px;
        }
        .btn-try-again {
          padding:8px 22px; border-radius:20px; border:none;
          background:#8b2020; color:#fff; font-size:14px; font-weight:700;
          font-family:'Baloo 2',sans-serif; cursor:pointer;
          -webkit-tap-highlight-color:transparent;
        }
        .btn-try-again:active { opacity:.8; }

        @keyframes fadeIn { from{opacity:0;transform:translateY(4px)} to{opacity:1;transform:none} }

        .nav-row { display:flex; align-items:center; gap:16px; margin-top:16px; }
        .btn-nav {
          width:50px; height:50px; border-radius:50%; border:none;
          background:white; box-shadow:0 3px 14px rgba(0,0,0,0.12);
          cursor:pointer; font-size:22px;
          display:flex; align-items:center; justify-content:center;
          transition:all .2s;
          -webkit-tap-highlight-color:transparent; touch-action:manipulation;
        }
        .btn-nav:hover:not(:disabled) { background:#1a7a4a; color:white; }
        .btn-nav:disabled { opacity:.3; cursor:not-allowed; }

        .dot-row {
          display:flex; gap:8px; flex-wrap:wrap;
          justify-content:center; align-items:center; max-width:260px;
        }
        .dot {
          width:12px; height:12px; border-radius:50%;
          background:#c8cdd6; border:2px solid #b0b8c4;
          cursor:pointer; transition:all .3s ease; flex-shrink:0;
          position:relative;
        }
        .dot::after {
          content:''; position:absolute;
          top:-10px; left:-10px; right:-10px; bottom:-10px;
        }
        .dot.active   { width:28px; border-radius:6px; background:#1a7a4a; border-color:#1a7a4a; }
        .dot.done     { background:#a5d6a7; border-color:#a5d6a7; }
        .dot.ap-dot   { background:#f59e0b; border-color:#f59e0b; }
        .dot.ap-dot.active { background:#f59e0b; border-color:#f59e0b; }

        .slide-footer { margin-top:14px; font-size:12px; color:#aaa; text-align:center; padding:0 16px; }

        @media (max-width:480px) {
          .question-text { font-size:44px; }
          .slide-card    { border-radius:20px; }
        }
      `}</style>

      <div className="slide-root">

        {/* ── Top Bar ── */}
        <div className="top-bar">
          <div className="top-bar-left">
            <button className="tb-btn" onClick={() => { setAutoPlay(false); navigate(-1); }}>
              🏠
            </button>
            <div className="tb-score">
              ✅ {completedCount}/{slides.length}
            </div>
          </div>
          <div style={{ fontSize:14, fontWeight:700, color:"rgba(255,255,255,0.9)" }}>
            {studentName}
          </div>
          <div className="top-bar-right">
            <button className="tb-btn" onClick={handleFinishSession}>
              🏁 Finish
            </button>
          </div>
        </div>

        {/* ── Progress bar ── */}
        <div className="prog-bar-wrap">
          <div className="prog-bar-fill" style={{ width:`${progressPct}%` }} />
        </div>

        {/* ── Header ── */}
        <div className="slide-header">
          <div className="institution">{slide.institution_name} · {slide.affiliation}</div>
          <div className="subject">{slide.subject_name}</div>
          <div className="progress">
            Slide {current + 1} of {slides.length}
            {autoPlay ? " · 🔄 Auto Playing…" : ` · ${slide.updated_ago}`}
          </div>
        </div>

        <div className="autoplay-bar">
          <div className="autoplay-bar-fill" style={{ width:`${progressPct}%` }} />
        </div>

        {/* ── Card ── */}
        <div className="slide-card" key={current}>

          {/* Picture */}
          <div className="pic-area">
            {slide.picture ? (
              <img
                src={`${IMG_BASE}${slide.picture}`}
                alt={slide.question_text}
                onError={e => { e.target.style.display = "none"; }}
              />
            ) : (
              <div className="no-img" style={{ background:cardColor }}>
                {slide.question_text}
              </div>
            )}
          </div>

          {/* Text + attempt badge */}
          <div className="question-area">
            <div className="question-text">{slide.question_text}</div>
            <span className="medium-badge" style={{
              background: isTamil ? "#fff0e6" : "#e8f0ff",
              color:      isTamil ? "#8b4513" : "#1a3a8b",
            }}>
              {slide.medium}
            </span>
            {slideAttempts > 0 && (
              <div>
                <span className="attempt-tag">
                  🔄 Attempt {slideAttempts + 1}
                </span>
              </div>
            )}
          </div>

          {/* Voices loading notice */}
          {!voicesReady && (
            <div className="voices-notice">⏳ Loading voice engine…</div>
          )}

          {/* Buttons */}
          <div className="actions">
            <button
              className={`btn btn-read ${speaking && !autoPlay ? "speaking":""}`}
              onClick={handleRead}
              disabled={autoPlay || !voicesReady || autoAdvancing}
            >
              🔊 {speaking && !autoPlay ? "Reading…" : "Read"}
            </button>

            {micSupported ? (
              <button
                className={`btn btn-mic ${listening ? "listening":""}`}
                onClick={listening ? stopListening : startListening}
                disabled={autoPlay || autoAdvancing}
              >
                🎤 {listening ? "Stop" : "Speak"}
              </button>
            ) : (
              <button
                className="btn btn-mic" disabled
                title={
                  window.location.protocol !== "https:" && window.location.hostname !== "localhost"
                    ? "Mic needs HTTPS"
                    : isIOS()
                    ? "Mic needs Safari 14.5+"
                    : "Mic not supported"
                }
                style={{ opacity:0.45, cursor:"not-allowed", fontSize:12 }}
              >
                🎤 {isIOS() && window.location.protocol !== "https:" ? "HTTPS needed" : "Not Supported"}
              </button>
            )}

            {micDenied && (
              <div style={{
                width:"100%", textAlign:"center", fontSize:12,
                color:"#c0392b", marginTop:-8, marginBottom:4
              }}>
                ⚠️ Mic permission denied. Go to Settings → Safari → Microphone → Allow.
              </div>
            )}

            <button
              className={`btn btn-autoplay ${autoPlay ? "playing":""}`}
              onClick={toggleAutoPlay}
              disabled={!voicesReady && !autoPlay}
            >
              {autoPlay ? "⏹ Stop" : "▶ Auto"}
            </button>

            {/* Skip button */}
            {!autoPlay && !autoAdvancing && (
              <button className="btn btn-skip" onClick={handleSkip}>
                ⏭ Skip
              </button>
            )}
          </div>

          {/* Autoplay reading indicator */}
          {autoPlay && speaking && (
            <div className="autoplay-reading">
              🔊 Reading slide {current + 1} of {slides.length}…
            </div>
          )}

          {/* Auto-advancing notice */}
          {autoAdvancing && (
            <div className="advancing-notice">
              ✅ Correct! Next slide in a moment…
            </div>
          )}

          {/* Speech recognition box */}
          {!autoPlay && (spokenText || listening) && (
            <div className="speech-box">
              <div className="speech-label">Your reading</div>
              <div className="speech-input">
                {spokenText || <span style={{ color:"#aaa", fontSize:16 }}>Listening…</span>}
              </div>
              {spokenText && <WordDiff expected={expected} spoken={spokenText.trim()} />}
              <div className="speech-controls">
                {spokenText.trim().split(/\s+/).filter(Boolean).length > 1 && (
                  <button className="btn-sm btn-del-word" onClick={deleteLastWord}>
                    ⌫ Delete last word
                  </button>
                )}
                <button className="btn-sm btn-clear" onClick={clearSpoken}>
                  ✕ Clear
                </button>
              </div>
            </div>
          )}

          {/* Result: correct */}
          {!autoPlay && resultMsg === "correct" && !autoAdvancing && (
            <div className="result-banner result-correct">
              ✅ நீங்கள் சரியாக படித்தீர்கள்! You read correctly!
            </div>
          )}

          {/* Result: wrong + Try Again */}
          {!autoPlay && resultMsg === "wrong" && (
            <div className="result-banner result-wrong">
              <span>❌ மீண்டும் முயற்சிக்கவும். Try again!</span>
              <button className="btn-try-again" onClick={handleTryAgain}>
                🔄 Try Again
              </button>
            </div>
          )}

        </div>{/* end card */}

        {/* ── Navigation ── */}
        <div className="nav-row">
          <button className="btn-nav" onClick={prev}
            disabled={current === 0 || autoPlay || autoAdvancing}>‹</button>

          <div className="dot-row">
            {slides.map((_, i) => {
              const isDone    = sessionLog.some(r => r.slideIndex === i && r.correct);
              const isCurrent = i === current;
              let cls = "dot";
              if (isCurrent) cls += " active";
              if (isDone && !isCurrent) cls += " done";
              if (autoPlay && isCurrent) cls += " ap-dot";
              return (
                <div key={i} className={cls}
                  onClick={() => { if (!autoPlay && !autoAdvancing) setCurrent(i); }}
                />
              );
            })}
          </div>

          <button className="btn-nav" onClick={next}
            disabled={current === slides.length - 1 || autoPlay || autoAdvancing}>›</button>
        </div>

        <div className="slide-footer">
          👩‍🏫 {slide.teacher_name} · {slide.grade_name}
          {autoPlay && <span style={{ marginLeft:8, color:"#f59e0b", fontWeight:700 }}>● Auto Playing</span>}
        </div>

      </div>
    </>
  );
}
